// frontend/src/modules/expenses/pages/CategoriesPage.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X, Check, Tag, ArchiveRestore } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { useApi } from '../../../design-system/useApi';
import { VISUAL } from '../../../design-system/visual';
import { Eyebrow, GlassCard } from '../../../design-system/pds';
import { expenseCategoriesApi } from '../api';
import type { Category, CategoryKind } from '../types';

export function CategoriesPage() {
  const [showInactive, setShowInactive] = React.useState(false);
  const { data, loading, reload } = useApi<{ rows: Category[] }>(
    `/api/expenses/categories${showInactive ? '?include_inactive=true' : ''}`,
  );
  const categories = data?.rows ?? [];

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [restoringId, setRestoringId] = React.useState<string | null>(null);

  async function reactivate(cat: Category) {
    setRestoringId(cat.id);
    try {
      await expenseCategoriesApi.update(cat.id, { is_active: true });
      toast.success(`"${cat.name}" reactivated`);
      reload();
    } catch (err: any) {
      toast.error('Reactivate failed', { description: err.message });
    } finally {
      setRestoringId(null);
    }
  }

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow={
          <>
            <Link to="/dashboard/expenses" className="hover:text-foreground transition-colors">Expenses</Link>
            {' / Categories'}
          </>
        }
        title="Expense categories"
        description="Buckets your team picks from when logging expenses. Codes help with accounting exports."
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/dashboard/expenses/approval-chains"
              className="text-[12px] font-semibold text-ink/65 dark:text-white/65 hover:text-coral-600 transition-colors px-2"
            >
              Approval chains →
            </Link>
            <Button size="default" className="gap-1.5" onClick={() => setAdding(true)} disabled={adding}>
              <Plus className="h-3.5 w-3.5" /> New category
            </Button>
          </div>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-5">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="h-4 w-4 rounded border-border accent-saffron"
          />
          <span className="text-[12.5px] font-semibold text-ink/65 dark:text-white/65">Show deleted categories</span>
        </label>

        {adding && (
          <CategoryEditorRow
            mode="create"
            onCancel={() => setAdding(false)}
            onSaved={() => { setAdding(false); reload(); }}
          />
        )}

        <GlassCard padding="p-0" className="overflow-hidden">
          {loading ? (
            <ListSkeleton />
          ) : categories.length === 0 && !adding ? (
            <EmptyState onAdd={() => setAdding(true)} />
          ) : (
            <ul className="divide-y divide-ink/6 dark:divide-white/8">
              {categories.map((cat) =>
                editingId === cat.id ? (
                  <li key={cat.id} className="px-5 py-4">
                    <CategoryEditorRow
                      mode="edit"
                      category={cat}
                      onCancel={() => setEditingId(null)}
                      onSaved={() => { setEditingId(null); reload(); }}
                    />
                  </li>
                ) : (
                  <motion.li
                    key={cat.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={['flex items-center gap-4 px-5 py-4', cat.is_active === false ? 'opacity-70' : ''].join(' ')}
                  >
                    <div className="h-10 w-10 rounded-2xl bg-coral/12 flex items-center justify-center shrink-0">
                      <Tag className="h-4 w-4 text-coral-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14px] font-semibold text-ink dark:text-white truncate">
                        {cat.name}
                        {cat.kind !== 'general' && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-coral/12 text-coral-600 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                            {cat.kind === 'mileage' ? 'Mileage' : 'Per-diem'}
                          </span>
                        )}
                      </p>
                      <p className="text-[12px] text-ink/55 dark:text-white/55 mt-0.5">
                        <span className="font-mono">{cat.code}</span>
                        {cat.kind !== 'general' && cat.rate != null && (
                          <span className="ml-2">· {cat.rate} per {cat.unit ?? 'unit'}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5">
                      {cat.is_active !== false ? (
                        <>
                          <button
                            type="button"
                            onClick={() => setEditingId(cat.id)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink/55 hover:text-ink dark:text-white/55 dark:hover:text-white hover:bg-white/60 dark:hover:bg-white/10 transition-colors"
                            aria-label={`Edit ${cat.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <DeleteCategoryButton category={cat} onDeleted={reload} />
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center rounded-full bg-ink/8 dark:bg-white/10 text-ink/55 dark:text-white/55 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide">
                            Deleted
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={restoringId === cat.id}
                            onClick={() => reactivate(cat)}
                            className="rounded-full gap-1.5 text-[12px] font-semibold"
                          >
                            <ArchiveRestore className="h-3.5 w-3.5" />
                            {restoringId === cat.id ? 'Restoring…' : 'Reactivate'}
                          </Button>
                        </>
                      )}
                    </div>
                  </motion.li>
                ),
              )}
            </ul>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

/* ───────────────────────────── inner bits ───────────────────────────── */

function CategoryEditorRow({
  mode,
  category,
  onCancel,
  onSaved,
}: {
  mode: 'create' | 'edit';
  category?: Category;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = React.useState(category?.name ?? '');
  const [code, setCode] = React.useState(category?.code ?? '');
  const [kind, setKind] = React.useState<CategoryKind>(category?.kind ?? 'general');
  const [rate, setRate] = React.useState<string>(category?.rate != null ? String(category.rate) : '');
  const [unit, setUnit] = React.useState<string>(category?.unit ?? '');
  const [busy, setBusy] = React.useState(false);

  const rateBased = kind !== 'general';

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !code.trim()) {
      toast.error('Name and code are required');
      return;
    }
    if (rateBased) {
      if (!(Number(rate) > 0)) { toast.error('Enter a positive rate'); return; }
      if (!unit.trim()) { toast.error('Enter a unit (e.g. km, mile, day)'); return; }
    }
    const payload = {
      name: name.trim(),
      code: code.trim(),
      kind,
      rate: rateBased ? Number(rate) : null,
      unit: rateBased ? unit.trim() : null,
    };
    setBusy(true);
    try {
      if (mode === 'create') {
        await expenseCategoriesApi.create(payload);
        toast.success('Category created');
      } else if (category) {
        await expenseCategoriesApi.update(category.id, payload);
        toast.success('Category updated');
      }
      onSaved();
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className={[VISUAL.bento, 'p-4 sm:p-5 space-y-3'].join(' ')}>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px_180px] gap-3">
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.12em]">Name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Travel, Mileage, Per-diem…" autoFocus required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.12em]">Code</Label>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="TRV-01" className="font-mono" required />
        </div>
        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-[0.12em]">Type</Label>
          <Select value={kind} onValueChange={(v) => setKind(v as CategoryKind)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">General (free amount)</SelectItem>
              <SelectItem value="mileage">Mileage (per distance)</SelectItem>
              <SelectItem value="per_diem">Per-diem (per day)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {rateBased && (
        <div className="grid grid-cols-1 sm:grid-cols-[180px_180px] gap-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.12em]">Rate (per unit)</Label>
            <Input
              type="number" min={0} step="0.0001" inputMode="decimal"
              value={rate} onChange={(e) => setRate(e.target.value)}
              placeholder="0.655" className="font-mono tabular-nums text-right" required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] uppercase tracking-[0.12em]">Unit</Label>
            <Input value={unit} onChange={(e) => setUnit(e.target.value)} placeholder={kind === 'mileage' ? 'km' : 'day'} required />
          </div>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" size="default" onClick={onCancel} disabled={busy} className="gap-1.5">
          <X className="h-3.5 w-3.5" /> Cancel
        </Button>
        <Button type="submit" size="default" disabled={busy} className="gap-1.5">
          <Check className="h-3.5 w-3.5" />
          {busy ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

function DeleteCategoryButton({ category, onDeleted }: { category: Category; onDeleted: () => void }) {
  const confirm = useConfirm();
  const [busy, setBusy] = React.useState(false);
  async function remove() {
    if (!(await confirm({
      title: `Delete "${category.name}"?`,
      description: 'Expenses in this category will keep their snapshot name, but the bucket goes away.',
      destructive: true,
      confirmLabel: 'Delete',
    }))) return;
    setBusy(true);
    try {
      await expenseCategoriesApi.remove(category.id);
      toast.success('Category deleted');
      onDeleted();
    } catch (err: any) {
      toast.error('Delete failed', { description: err.message });
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      type="button"
      onClick={remove}
      disabled={busy}
      aria-label={`Delete ${category.name}`}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full text-ink/55 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-50 transition-colors"
    >
      <Trash2 className="h-3.5 w-3.5" />
    </button>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-ink/6 dark:divide-white/8">
      {Array.from({ length: 4 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-10 w-10 rounded-2xl bg-pearl-200 dark:bg-white/10 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-pearl-200 dark:bg-white/10 animate-pulse" />
            <div className="h-2.5 w-24 rounded bg-pearl-100 dark:bg-white/5 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="py-16 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-16 w-16 rounded-3xl bg-coral/12 flex items-center justify-center">
        <Tag className="h-6 w-6 text-coral-600" strokeWidth={1.6} />
      </div>
      <Eyebrow className="mb-2">No categories yet</Eyebrow>
      <p className="text-[20px] font-semibold text-ink dark:text-white tracking-[-0.015em]">
        Start with a few buckets
      </p>
      <p className="text-[14px] text-ink/60 dark:text-white/60 mt-2 leading-relaxed">
        Categories let your team file expenses cleanly. Try Travel, Meals, Software to start.
      </p>
      <div className="mt-6">
        <Button onClick={onAdd} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add category
        </Button>
      </div>
    </div>
  );
}
